import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, requiredField } from '../../../../utils/validators';
import { WeezHeader, Button, ToastContext, SplitContainer, Loader } from '@weezevent/nacre';
import Widget from '../../../../models/widget';
import WidgetGeneralInformation from '../../components/settings/widgetGeneralInformation';
import WidgetConfiguration from '../../components/settings/widgetConfiguration';
import WidgetAdvancedConfiguration from '../../components/settings/widgetAdvancedConfiguration';
import WidgetPreviewButton from '../../components/widget-preview-button';
import { WidgetPreview } from '../../components/pages/new-widget';
import WidgetDesign from '../../components/settings/widget-design/widget-design';
import { usePermissions } from '../../../../user/permissions';
import Style from './settings.module.css';

export default function PersonalSpaceSettings({ organization, personalSpaceId }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [submitting, setSubmitting] = React.useState(false);
    const [isJsonValid, setIsJsonValid] = React.useState(false);
    const permissions = usePermissions();

    const validators = React.useMemo(() => {
        return {
            name: requiredField,
            default_language: requiredField
        };
    }, []);

    const [form, setForm, setFormObject, formErrors] = useForm(
        {
            id: null,
            name: '',
            default_language: 'en-gb',
            languages: [],
            events: [],
            customs: null,
            widget_config: {}
        },
        validators
    );

    const [widget, loadingWidget, sync] = Widget.useApiModel(
        {
            organizationId: organization.pk,
            id: personalSpaceId,
            query: {
                usage__in: ['personal_space', 'moderation']
            },
            onSuccess: _widget => {
                setFormObject({
                    id: _widget.id,
                    name: _widget.name,
                    default_language: _widget.default_language,
                    languages: _widget.languages,
                    events: _widget.events.map(event => event.id),
                    customs: _widget.customs,
                    widget_config: _widget.widget_config,
                    all_events: _widget.all_events
                });
            }
        },
        [personalSpaceId, organization]
    );

    const handleSubmit = React.useCallback(() => {
        setSubmitting(true);
        let new_widget = new Widget(form);
        if (form.all_events) {
            new_widget.events = []; // Clear events if all events is true
        }
        new_widget
            .save({ organizationId: organization.id })
            .then(() => {
                toast.success(t('ticket.personal_spaces.settings.toast-success'));
                sync();
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.personal_spaces.settings.toast-error'));
            })
            .finally(() => {
                setSubmitting(false);
            });
    }, [organization, form, toast, t]);

    if (loadingWidget) {
        return <Loader />;
    }

    return (
        <div className={Style['personal-space-container']}>
            <WeezHeader
                rightComponent={[
                    <WidgetPreviewButton key="preview" widget={widget} />,
                    <Button primary key="save" label={t('ticket.common.action.save')} onClick={handleSubmit} disabled={submitting || !isJsonValid} />
                ]}
                title={t('ticket.personal_spaces.settings.title')}
            />
            <SplitContainer size="maxi" additional={<WidgetPreview widget={widget} />}>
                <WidgetGeneralInformation form={form} setForm={setForm} formErrors={formErrors} />
                <WidgetConfiguration organization={organization} form={form} setForm={setForm} setFormObject={setFormObject} formErrors={formErrors} usage={widget.usage} />
                <WidgetDesign organization={organization} form={form} onChange={setForm} allowedCapacities={['PyvarCapacity.PERSONALSPACE_ADMIN']} />

                {permissions.hasCapacities(['PyvarCapacity.PERSONALSPACE_ADMIN'], organization.id) && (
                    <WidgetAdvancedConfiguration form={form} setForm={setForm} setIsJsonValid={setIsJsonValid} />
                )}
            </SplitContainer>
        </div>
    );
}
